import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import { formatDateToISOWeek } from '@util/date-util'

import { WerklijstTile } from '../../../aansluiting/dashboard/components/werklijsten/WerklijstTile'
import type { ProcesstapHoofdleiding } from '../../../status/types/ProcesstapHoofdleiding'
import { Grid } from '../../../theme/components/Grid'
import { initialState as initialStateActiviteiten } from '../../activiteiten/services/searchActiviteiten'
import { initialState as initialStateAssignedBijlagen } from '../../bijlagen/services/searchAssignedBijlagen'
import { useWerklijsten } from '../hooks/useWerklijsten'
import type { Werklijst } from '../types/Werklijst'

import { WerklijstenFilters } from './WerklijstenFilters'

interface Props {
  onClickTile: (query: any, url: any) => void
  filterOptions: {
    coordinerenden: string[]
    labels: FilterOption[]
    hoofdleiding: {
      gebieden: FilterOption[]
      netbeheerders: string[]
      verantwoordelijken: GroupedFilterOption[]
      typeWerkTypes: string[]
      gemeenten: string[]
    }
    processtappen: ProcesstapHoofdleiding[]
  }
}

export const Werklijsten = ({ onClickTile, filterOptions }: Props) => {
  const {
    data: { activiteiten, bijlagen },
    filters,
    initialFilters,
    onChangeFilters,
    onRefresh,
    onReset,
  } = useWerklijsten()

  const onClickTileBijlagen = (werklijst: Werklijst, kpiColor?: string) => {
    const { label } = werklijst

    const { fromDate, toDate, ...rest } = filters

    const query = {
      filters: {
        ...initialStateAssignedBijlagen.query.filters,
        ...rest,
        documentsoort: [label],
        assignedTo: filters.organisatie ? [filters.organisatie] : [],
        fromWeek: formatDateToISOWeek(fromDate) || '',
        toWeek: formatDateToISOWeek(toDate) || '',
        kpi: kpiColor,
      },
      page: initialStateAssignedBijlagen.query.page,
      sort: initialStateAssignedBijlagen.query.sort,
    }

    onClickTile(query, '/hoofdleiding/bijlagen')
  }

  const onClickTileActiviteiten = (werklijst: Werklijst, kpiColor?: string) => {
    const { code, label } = werklijst

    const query = {
      filters: {
        ...initialStateActiviteiten.query.filters,
        ...filters,
        type: [{ code, label }],
        assignedTo: filters.organisatie ? [filters.organisatie] : [],
        kpi: kpiColor,
      },
      page: initialStateActiviteiten.query.page,
      sort: initialStateActiviteiten.query.sort,
    }

    onClickTile(query, '/hoofdleiding/activiteiten')
  }

  return (
    <>
      <WerklijstenFilters
        filters={filters}
        onChange={onChangeFilters}
        initialFilters={initialFilters}
        onRefresh={onRefresh}
        onReset={onReset}
        filterOptions={filterOptions}
      />
      <Box sx={{ p: '50px', ml: '20px' }}>
        <Accordion sx={{ width: '100%' }} defaultExpanded key="bijlagen">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: '24px', pl: '24px' }}>
              Bijlagen
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {bijlagen.map((werklijst) => (
                <Grid key={werklijst.label}>
                  <WerklijstTile
                    werklijst={werklijst}
                    onClickTile={onClickTileBijlagen}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ width: '100%' }} defaultExpanded key="Activiteiten">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: '24px', pl: '24px' }}>
              Activiteiten
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {activiteiten.map((werklijst) => (
                <Grid key={werklijst.label}>
                  <WerklijstTile
                    werklijst={werklijst}
                    onClickTile={onClickTileActiviteiten}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  )
}
